import React from 'react';
import { Text, mergeClasses } from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { FormField } from '@components/common/FormField';
import { FilmingApprovalPanel } from '@components/common/FilmingApprovalPanel';
import { CaterersPanel } from '@components/common/CaterersPanel';
import { useFormStepStyles } from './FormStep.styles';
import type { FormStep as FormStepModel, FieldDefinition } from '@store/models/booking';
import type { Building } from '@store/models/buildings/entities';

/**
 * Section glyphs are imported as raw SVG markup (?raw). The source files ship
 * with a hard-coded navy fill that is invisible in dark mode, so we strip the
 * baked-in hex colors and let the glyph inherit `currentColor` (driven by the
 * themed `sectionIconFg` token). This keeps the icon the same blue as the
 * booking stepper number circle in both light and dark modes (spec 101).
 */
const tintSvg = (raw: string): string =>
  raw.replace(/(fill|stroke)="#[0-9a-fA-F]{3,8}"/g, '$1="currentColor"');

export interface FormStepProps {
  step: FormStepModel;
  formData: Record<string, unknown>;
  validationErrors: Record<string, string>;
  onChange: (key: string, value: unknown) => void;
  selectedBuilding?: Building | null;
}

export const FormStep: React.FC<FormStepProps> = ({
  step,
  formData,
  validationErrors,
  onChange,
  selectedBuilding,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useFormStepStyles();

  const renderFields = (fields: FieldDefinition[]) => (
    <div className={styles.fieldsGrid}>
      {fields.map((field) => (
        <React.Fragment key={field.key}>
          <div className={field.colspan === 2 ? styles.colSpan2 : undefined}>
            <FormField
              field={field}
              value={formData[field.key]}
              onChange={onChange}
              error={validationErrors[field.key] ?? ''}
              formData={formData}
            />
          </div>
          {field.key === 'hasCatering' &&
            formData.hasCatering === 'yes' &&
            selectedBuilding &&
            (selectedBuilding.caterers?.length ?? 0) > 0 && (
              <div className={styles.colSpan2}>
                <CaterersPanel caterers={selectedBuilding.caterers} />
              </div>
            )}
          {field.key === 'filmingRequired' &&
            formData.filmingRequired === 'yes' &&
            selectedBuilding && (
              <div className={styles.colSpan2}>
                <FilmingApprovalPanel
                  filmingFormUrl={selectedBuilding.filmingFormUrl}
                  gsocEmail={selectedBuilding.gsocEmail}
                  regionalSecurityAdvisorEmail={selectedBuilding.regionalSecurityAdvisorEmail}
                  siteContactEmail={selectedBuilding.siteContactEmail}
                />
              </div>
            )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className={mergeClasses(styles.root, styles.rootFigma)}>
      {step.descriptionKey && !step.sections && (
        <Text className={styles.stepDescription}>{t(step.descriptionKey)}</Text>
      )}

      {step.sections
        ? step.sections.map((section, sectionIdx) => {
            const isFigmaSection = !!section.iconSrc;
            return (
              <div
                key={section.headerKey ?? `section-${sectionIdx}`}
                className={
                  isFigmaSection
                    ? styles.sectionRowFigma
                    : section.background === 'light-blue'
                      ? styles.sectionLightBlue
                      : styles.sectionWrapper
                }
              >
                {isFigmaSection ? (
                  <>
                    <div className={styles.sectionHeaderArea}>
                      <span
                        aria-hidden
                        className={styles.sectionHeaderIcon}
                        dangerouslySetInnerHTML={{ __html: tintSvg(section.iconSrc ?? '') }}
                      />
                      <div className={styles.sectionHeaderText}>
                        {section.headerKey && (
                          <Text as="h3" className={styles.sectionTitleFigma}>
                            {t(section.headerKey)}
                          </Text>
                        )}
                        {section.descriptionKey && (
                          <Text className={styles.sectionDescriptionFigma}>
                            {t(section.descriptionKey)}
                          </Text>
                        )}
                      </div>
                    </div>
                    <div className={styles.sectionFieldsArea}>
                      {renderFields(section.fields)}
                      {section.tipKey && (
                        <div className={styles.sectionTip} role="note">
                          <InfoRegular className={styles.sectionTipIcon} />
                          <Text className={styles.sectionTipText}>{t(section.tipKey)}</Text>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {section.headerKey && (
                      <Text as="h3" className={styles.sectionHeader}>
                        {t(section.headerKey)}
                      </Text>
                    )}
                    {renderFields(section.fields)}
                  </>
                )}
              </div>
            );
          })
        : step.fields && renderFields(step.fields)}
    </div>
  );
};

FormStep.displayName = 'FormStep';
